import * as Dfns from 'date-fns/fp';
import {
  CompanyMetadata,
  NetworkMap,
  NetworkMetadata
} from '@blisspointmedia/bpm-types/dist/LogsTracker';
import { DATE_FORMAT } from '@src/utils/date-utils';
import {
  LogStatus,
  LogStatusType,
  LogsTrackerRow,
  LogType,
  LogTypeType,
  NetworkEdit,
  NetworkEdits
} from './logs-tracker-constants';

const MAX_MISSING_SHOWN = 3;

// Pre logs are worked during the week they cover; post logs are worked the week after. Computed
// per call (not at module load) so a tab left open past Monday still defaults to the right week.
export const getDefaultWeek = (type: LogTypeType, today = new Date()): string => {
  const weekStart = Dfns.startOfISOWeek(today);
  return Dfns.format(DATE_FORMAT, type === LogType.PRE ? weekStart : Dfns.subWeeks(1, weekStart));
};

export const buildNetworkMap = (rows: LogsTrackerRow[]): NetworkMap => {
  const networkMap: NetworkMap = {};
  for (const row of rows) {
    networkMap[row.network] ??= {
      network: row.network,
      networkNotes: row.network_notes ?? '',
      companies: {}
    };
    networkMap[row.network].companies[row.company] = {
      company: row.company,
      companyNotes: row.company_notes ?? '',
      checked: Boolean(row.checked),
      didNotRun: Boolean(row.didNotRun)
    };
  }
  return networkMap;
};

export const applyEdits = (networkMap: NetworkMap, edits: NetworkEdits): NetworkMap =>
  Object.fromEntries(
    Object.entries(networkMap).map(([name, network]) => {
      const edit = edits[name];
      if (!edit) {
        return [name, network];
      }
      const companies = Object.fromEntries(
        Object.entries(network.companies).map(([company, metadata]) => [
          company,
          { ...metadata, ...edit.companies?.[company] }
        ])
      );
      return [
        name,
        { ...network, networkNotes: edit.networkNotes ?? network.networkNotes, companies }
      ];
    })
  );

export const addEdit = (edits: NetworkEdits, network: string, edit: NetworkEdit): NetworkEdits => {
  const current = edits[network] ?? {};
  const companies = { ...current.companies };
  for (const [company, patch] of Object.entries(edit.companies ?? {})) {
    companies[company] = { ...companies[company], ...patch };
  }
  return {
    ...edits,
    [network]: {
      ...current,
      ...(edit.networkNotes !== undefined && { networkNotes: edit.networkNotes }),
      companies
    }
  };
};

const isCompanyDone = ({ checked, didNotRun }: CompanyMetadata): boolean => checked || didNotRun;

// Completed: every company is logged in or marked DNR. In Progress: anything checked or noted.
export const getNetworkStatus = (network: NetworkMetadata): LogStatusType => {
  const companies = Object.values(network.companies);
  if (companies.every(isCompanyDone)) {
    return LogStatus.COMPLETED;
  }
  const isTouched =
    Boolean(network.networkNotes) ||
    companies.some(
      ({ companyNotes, checked, didNotRun }) => Boolean(companyNotes) || checked || didNotRun
    );
  return isTouched ? LogStatus.IN_PROGRESS : LogStatus.NOT_STARTED;
};

export const getProgress = (network: NetworkMetadata): { done: number; total: number } => {
  const companies = Object.values(network.companies);
  return { done: companies.filter(isCompanyDone).length, total: companies.length };
};

export const getMissingCompanies = (network: NetworkMetadata): string[] =>
  Object.values(network.companies)
    .filter(company => !isCompanyDone(company))
    .map(({ company }) => company);

export const formatMissingCompanies = (missing: string[]): string =>
  missing.length > MAX_MISSING_SHOWN
    ? `${missing.slice(0, MAX_MISSING_SHOWN).join(', ')} +${missing.length - MAX_MISSING_SHOWN} more`
    : missing.join(', ');

export const isAllDidNotRun = (network: NetworkMetadata): boolean =>
  Object.values(network.companies).every(({ didNotRun }) => didNotRun);

export const hasNotes = (network: NetworkMetadata): boolean =>
  Boolean(network.networkNotes) ||
  Object.values(network.companies).some(({ companyNotes }) => Boolean(companyNotes));

export const matchesSearch = (network: NetworkMetadata, search: string): boolean => {
  const query = search.trim().toLowerCase();
  return (
    !query ||
    network.network.toLowerCase().includes(query) ||
    Object.keys(network.companies).some(company => company.toLowerCase().includes(query))
  );
};

export const groupByStatus = (
  networks: NetworkMetadata[]
): Record<LogStatusType, NetworkMetadata[]> => {
  const groups: Record<LogStatusType, NetworkMetadata[]> = {
    [LogStatus.NOT_STARTED]: [],
    [LogStatus.IN_PROGRESS]: [],
    [LogStatus.COMPLETED]: []
  };
  for (const network of networks) {
    groups[getNetworkStatus(network)].push(network);
  }
  return groups;
};

const patchCompanies = (
  network: NetworkMetadata,
  getPatch: (company: CompanyMetadata) => Partial<CompanyMetadata> | null
): NetworkEdit => ({
  companies: Object.fromEntries(
    Object.values(network.companies).flatMap(company => {
      const patch = getPatch(company);
      return patch ? [[company.company, patch]] : [];
    })
  )
});

export const checkAll = (network: NetworkMetadata): NetworkEdit =>
  patchCompanies(network, () => ({ checked: true }));

export const clearAll = (network: NetworkMetadata): NetworkEdit =>
  patchCompanies(network, () => ({ checked: false, didNotRun: false }));

export const markRemainingDidNotRun = (network: NetworkMetadata): NetworkEdit =>
  patchCompanies(network, company => (isCompanyDone(company) ? null : { didNotRun: true }));
